// client/src/services/influxService.js
// SECURE VERSION: All InfluxDB queries now go through the server proxy
// This prevents exposing database credentials in the client

const API_BASE = process.env.REACT_APP_API_URL || ''
const SAFE_LOG = process.env.NODE_ENV !== 'production'

/**
 * Query sensor data from InfluxDB via server proxy
 * @param {string} sensorType - measurement name (e.g., 'procv_co2', 'CTD')
 * @param {Array<string>} selectedFields - array of _field names to include (optional)
 * @param {string} timeRange - Flux time range (e.g., '-6h')
 * @param {string|null} selectedBuoy - single buoy id (optional)
 * @param {Array<string>|null} selectedBuoys - multiple buoy ids (optional)
 * @returns {Promise<Array<Object>>}
 */
export async function querySensorData(
  sensorType = 'sensor_data',
  selectedFields = [],
  timeRange = '-1h',
  selectedBuoy = null,
  selectedBuoys = null,
  timeStop = null
) {
  try {
    const response = await fetch(`${API_BASE}/api/influx/query`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sensorType,
        selectedFields,
        timeRange,
        selectedBuoy,
        selectedBuoys,
        timeStop
      })
    })

    if (!response.ok) {
      throw new Error(`Failed to query sensor data: ${response.statusText}`)
    }

    const results = await response.json()

    if (!results.length && SAFE_LOG) {
      console.warn(`⚠️ No results: measurement=${sensorType}, timeRange=${timeRange}, buoys=${JSON.stringify(selectedBuoys || selectedBuoy || 'ALL')}, fields=${selectedFields?.join(',') || 'ALL'}`)
    }

    return results
  } catch (error) {
    console.error('Error querying sensor data:', error)
    throw error
  }
}

/**
 * List distinct buoy IDs seen in the time range via server proxy
 * @param {string} timeRange
 * @returns {Promise<string[]>}
 */
export async function listBuoys(timeRange = '-30d') {
  try {
    const response = await fetch(`${API_BASE}/api/influx/buoys?timeRange=${encodeURIComponent(timeRange)}`)

    if (!response.ok) {
      throw new Error(`Failed to list buoys: ${response.statusText}`)
    }

    const results = await response.json()
    if (SAFE_LOG) console.log('🪪 listBuoys:', results)
    return results
  } catch (error) {
    console.error('Error listing buoys:', error)
    throw error
  }
}

/**
 * List distinct measurement names seen in the time range via server proxy
 * @param {string} timeRange
 * @returns {Promise<string[]>}
 */
export async function listMeasurements(timeRange = '-30d') {
  try {
    const response = await fetch(`${API_BASE}/api/influx/measurements?timeRange=${encodeURIComponent(timeRange)}`)

    if (!response.ok) {
      throw new Error(`Failed to list measurements: ${response.statusText}`)
    }

    const results = await response.json()
    if (SAFE_LOG) console.log('📏 listMeasurements:', results)
    return results
  } catch (error) {
    console.error('Error listing measurements:', error)
    throw error
  }
}

/**
 * List available sensors (measurements) for specific buoys within a time range via server proxy
 * @param {Array<string>} buoyIds - Array of buoy IDs
 * @param {string} timeRange - Time range string
 * @returns {Promise<Object>} Object with sensor availability per buoy
 */
export async function listAvailableSensors(buoyIds = [], timeRange = '-30d') {
  if (!buoyIds.length) return {}

  try {
    const response = await fetch(`${API_BASE}/api/influx/sensors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ buoyIds, timeRange })
    })

    if (!response.ok) {
      throw new Error(`Failed to list available sensors: ${response.statusText}`)
    }

    const results = await response.json()
    if (SAFE_LOG) console.log('🔧 listAvailableSensors:', results)
    return results
  } catch (error) {
    console.error('Error listing available sensors:', error)
    throw error
  }
}

/**
 * List available fields for specific sensors and buoys within a time range via server proxy
 * @param {Array<string>} buoyIds - Array of buoy IDs
 * @param {Array<string>} measurements - Array of measurement names
 * @param {string} timeRange - Time range string
 * @returns {Promise<Object>} Object with field availability per buoy per sensor
 */
export async function listAvailableFields(buoyIds = [], measurements = [], timeRange = '-30d') {
  if (!buoyIds.length || !measurements.length) return {}

  try {
    const response = await fetch(`${API_BASE}/api/influx/fields`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ buoyIds, measurements, timeRange })
    })

    if (!response.ok) {
      throw new Error(`Failed to list available fields: ${response.statusText}`)
    }

    const results = await response.json()
    if (SAFE_LOG) console.log('📋 listAvailableFields:', results)
    return results
  } catch (error) {
    console.error('Error listing available fields:', error)
    throw error
  }
}

/**
 * Get complete buoy availability data in one efficient query via server proxy
 * Only returns buoys that have data with their full sensor/field structure
 * @param {string} timeRange - Time range string
 * @param {Array<string>} allowedBuoys - Whitelist of allowed buoy IDs
 * @returns {Promise<Object>} Complete availability structure ready for use
 */
export async function getCompleteAvailability(timeRange = '-7d', allowedBuoys = [], timeStop = null) {
  try {
    const response = await fetch(`${API_BASE}/api/influx/availability`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ timeRange, allowedBuoys, timeStop })
    })

    if (!response.ok) {
      throw new Error(`Failed to get complete availability: ${response.statusText}`)
    }

    const results = await response.json()
    if (SAFE_LOG) console.log('🚀 getCompleteAvailability:', results)
    return results
  } catch (error) {
    console.error('Error getting complete availability:', error)
    throw error
  }
}
